import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Box, Typography, List, Divider, IconButton, ButtonBase } from "@mui/material";
import { useNavigate, NavigateFunction } from "react-router-dom";
import { useInfoStore } from "../../../store/userInfo";
import { useMenuStore } from "../../../store/menuState";
import { useOrderStore } from "../../../store/order";
import { useKioskStore } from "../../../store/kiosk";
import { ICart, IOrderModifiers } from "../../../types/menu";
import { useTranslation } from 'react-i18next';
import { isBrowser } from 'react-device-detect';
import moment from "moment";
import _ from "lodash";
import { createOrder } from "../../../services/order.service";
import { debounce } from "lodash";
import { useWindowDimensions } from "../../../App";
import OrderButton from "./OrderButton";
import { useAnalytics } from "../../../analytics/AnalyticsContext";
import { safeStringify, preparePinpointData } from "../../../utils/jsonUtils";
import { logger } from "../../../utils/logger";

interface IMenu {
  id: number;
  index: number;
}
const isIOSSafari = /iPhone|iPad|iPod/i.test(navigator.userAgent) && /WebKit/i.test(navigator.userAgent) && !/(CriOS|FxiOS|OPiOS|mercury)/i.test(navigator.userAgent);

const Cart = () => {
  const { height, orientation } = useWindowDimensions();
  const { userInfo, tableInfo, currentLng } = useInfoStore();
  const storeId = userInfo?.storeId || "";
  const tableId = tableInfo?.id || 0;
  const isAyce = userInfo?.isAyce || false;
  const {t} =  useTranslation(`${storeId}`);
  const { recordOrderEvent } = useAnalytics();
  const { categories, setCategories, isNativeFlag, setTimerInfo, deleteTimerInfo } = useMenuStore()
  const { cart, setCart, totalCount, totalPrice, categoryInCart, setSnackMode, setShowSnackbar, deleteCart, setOpenCart, setOpenComplete, setCompleteList
    , setLimitValue, setCheckNewCart } = useOrderStore();
  const { setOpenDiningOption } = useKioskStore();
  const { setKioskOrderList } = useKioskStore();
  const { resetOrderCart } = useOrderStore();
  const navigate: NavigateFunction = useNavigate();
  const [clickOrder, setClickOrder] = useState(false);
  const timersRef = useRef<{[key: string]: {timeout: NodeJS.Timeout, interval: NodeJS.Timeout}}>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const lastClickTime = useRef(0);
  const isProcessing = useRef(false);
  const MIN_CLICK_INTERVAL = 50;
  const landMode = orientation === "landscape";

  const isClickAllowed = useCallback(() => {
    const now = Date.now();
    if (now - lastClickTime.current < MIN_CLICK_INTERVAL || isProcessing.current) {
      return false;
    }
    lastClickTime.current = now;
    return true;
  }, []);

  const optimizedHandleSubCount = useCallback((item: any) => {
    if (!isClickAllowed() || isUpdating) return;
    isProcessing.current = true;
    requestAnimationFrame(() => {
      setIsUpdating(true);
      handleSubCount(item);
      setIsUpdating(false);
      isProcessing.current = false;
    });
  }, [handleSubCount, isUpdating]);

  const optimizedHandleAddCount = useCallback((item: any) => {
    if (!isClickAllowed() || isUpdating) return;
    isProcessing.current = true;
    requestAnimationFrame(() => {
      setIsUpdating(true);
      handleAddCount(item);
      setIsUpdating(false);
      isProcessing.current = false;
    });
  }, [handleAddCount, isUpdating]);

  useEffect(() => {
    if(cart === undefined || cart.length === 0) return
    const hasNewItem = cart.some(item => item.newItem);
  
    if (hasNewItem) {
      const timerId = setTimeout(() => {
        setCheckNewCart(cart)
      }, 2000);
      return () => clearTimeout(timerId);
    }
  }, [cart]);

  // 컴포넌트 언마운트 시 모든 타이머 정리
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(({timeout, interval}) => {
        clearTimeout(timeout);
        clearInterval(interval);
      });
    };
  }, []);

  function handleSubCount(item: ICart): void {
    requestAnimationFrame(() => { 
      // logger.log('sub',item)
      if(item.quantity === item.minRequired) {
        handleDeleteItem(item)
      } else {
        const newItem = {...item, quantity: 1}
        setCart({ item: newItem, type: "sub"});
      }
    });
  }

  function handleAddCount(item: ICart): void {
    if(isAyce) {
      if(item.maxAllowedPerTable && item.quantity === item.maxAllowedPerTable) {
        setLimitValue(item!.maxAllowedPerTable - item.quantity)
        setSnackMode(12)
        setShowSnackbar(true)
        return
      } else if (item.categoryLimits?.maxAllowed && item.quantity === item.categoryLimits.maxAllowed) {
        setLimitValue(item!.categoryLimits.maxAllowed - item.quantity)
        setSnackMode(12)
        setShowSnackbar(true)
        return
      }
    } else {
      if(item.maxAllowed && item.quantity === item.maxAllowed) {
        setLimitValue(item!.maxAllowed - item.quantity)
        setSnackMode(11)
        setShowSnackbar(true)
        return
      }
    }
    
    requestAnimationFrame(() => { 
      const newItem = {...item, quantity: 1}
      setCart({ item: newItem, type: "add"});
    });
  }

  const handleDeleteItem = (deleteItem: ICart): void => {
    deleteCart(deleteItem)
  }

  function addTimeoutsByCategory(arr: any[], categories: any[]) {
    logger.log("addTimeoutsByCategory 함수 시작");
    
    // 기본 검증
    if (!Array.isArray(arr) || arr.length === 0) {
      logger.error("카테고리 배열이 비어있거나 유효하지 않습니다", arr);
      return;
    }
    
    if (!Array.isArray(categories) || categories.length === 0) {
      logger.error("타임아웃 정보가 없습니다", categories);
      return;
    }
    
    logger.log("카테고리 배열:", arr);
    logger.log("타이머 정보:", categories);
    
    // 기존 타이머 정리
    Object.values(timersRef.current).forEach(({timeout, interval}) => {
      clearTimeout(timeout);
      clearInterval(interval);
    });
    timersRef.current = {};
    
    // 카테고리에 타이머 설정
    let updatedCategories = [...arr];
    
    for (const timerInfo of categories) {
      if (!timerInfo.categoryId || !timerInfo.orderIntervalMinutes) {
        logger.log("유효하지 않은 타이머 정보:", timerInfo);
        continue;
      }
      
      const categoryIndex = updatedCategories.findIndex(cat => cat.id === timerInfo.categoryId);
      
      if (categoryIndex === -1) {
        logger.log(`ID가 ${timerInfo.categoryId}인 카테고리를 찾을 수 없습니다.`);
        continue;
      }
      
      const category = updatedCategories[categoryIndex];
      logger.log(`카테고리 ${timerInfo.categoryId}에 타이머 설정 시작`);
      
      const timeoutDuration = timerInfo.orderIntervalMinutes * 60 * 1000;
      const timeoutEnd = Date.now() + timeoutDuration;
      
      logger.log(`타이머 시간: ${timerInfo.orderIntervalMinutes}분, 종료 시간: ${new Date(timeoutEnd).toLocaleTimeString()}`);
      
      // 타임아웃 설정
      const timeoutId = setTimeout(() => {
        logger.log(`카테고리 ${category.id} 타이머 완료`);
        
        const currentCategories = [...updatedCategories];
        const index = currentCategories.findIndex(cat => cat.id === category.id);
        
        if (index !== -1) {
          logger.log(`타이머 완료 처리 - 카테고리 ID: ${category.id}`);
          
          // 타이머 정리
          if (timersRef.current[category.id]) {
            const { timeout, interval } = timersRef.current[category.id];
            clearTimeout(timeout);
            clearInterval(interval);
            delete timersRef.current[category.id];
          }
          
          // 카테고리 상태 업데이트
          currentCategories[index] = {
            ...currentCategories[index],
            timeoutEnd: undefined,
            remainingMinutes: undefined,
            timerCategoryId: undefined
          };
          
          logger.log("타이머 제거 후 카테고리:", currentCategories[index]);
          setCategories([...currentCategories]);
          deleteTimerInfo(category.id);
        }
      }, timeoutDuration);
      
      // 인터벌 설정
      const intervalId = setInterval(() => {
        const currentCategories = [...updatedCategories];
        const index = currentCategories.findIndex(cat => cat.id === category.id);
        
        if (index === -1) {
          logger.log(`카테고리 ${category.id}가 더 이상 존재하지 않습니다. 인터벌 정리`);
          if (timersRef.current[category.id]) {
            const { timeout, interval } = timersRef.current[category.id];
            clearTimeout(timeout);
            clearInterval(interval);
            delete timersRef.current[category.id];
          }
          return;
        }
        
        const now = Date.now();
        const remainingTime = timeoutEnd - now;
        
        if (remainingTime <= 0) {
          logger.log(`카테고리 ${category.id} 타이머 종료 - 시간 만료`);
          if (timersRef.current[category.id]) {
            const { timeout, interval } = timersRef.current[category.id];
            clearTimeout(timeout);
            clearInterval(interval);
            delete timersRef.current[category.id];
          }
          
          currentCategories[index] = {
            ...currentCategories[index],
            timeoutEnd: undefined,
            remainingMinutes: undefined,
            timerCategoryId: undefined
          };
          
          setCategories([...currentCategories]);
          return;
        }
        
        const minutes = Math.ceil(remainingTime / 60000);
        logger.log(`카테고리 ${category.id} 남은 시간: ${minutes}분 (${remainingTime}ms)`);
        currentCategories[index].remainingMinutes = minutes;
        setCategories([...currentCategories]);
      }, 1000);
      
      // 타이머 저장
      timersRef.current[category.id] = {
        timeout: timeoutId,
        interval: intervalId
      };
      
      // 카테고리 업데이트
      updatedCategories[categoryIndex] = {
        ...category,
        timeoutEnd,
        timerCategoryId: category.id,
        remainingMinutes: timerInfo.orderIntervalMinutes
      };

      setTimerInfo(category.id, {
        timeoutEnd,
        timerCategoryId: category.id,
        remainingMinutes: timerInfo.orderIntervalMinutes,
        orderIntervalMinutes: timerInfo.orderIntervalMinutes
      });
    }
    
    setCategories(updatedCategories);
  }

  const handleOrderMode = async (): Promise<void>  => {
            // logger.log('mode')

    if(tableInfo.isKioskMode) {
      const orderItems = cart;
      const totalAmount = totalPrice;
      const totalQuantity = totalCount;
      if(cart.length === 0) {
        setSnackMode(5)
        setShowSnackbar(true)
        setOpenCart(false)
        return;
      }
      setKioskOrderList({orderItems, tableId, totalAmount, totalQuantity, phoneNo: ''})
      setOpenDiningOption(true)
      navigate('/diningoption')

    } else {
      handleOrderNow()
      //debouncedOrder()
    }
  }
  const debouncedOrder = debounce(() => {
            // logger.log('debounceOrder')
    handleOrderNow()
  }, 300);

  const handleOrderNow = async (): Promise<void>  => {
          // logger.log("handleOrderNow")
    if(clickOrder) return;
    setClickOrder(true);

      const totalAmount = totalPrice;
      const totalQuantity = totalCount;
      const tableId = tableInfo.id;
      const tableName = tableInfo.tableName;
      const orderItems = cart;
      if(orderItems.length === 0) {
        setSnackMode(5)
        setShowSnackbar(true)
        setOpenCart(false)
        return;
      }

      try{
        // storeId가 문자열일 수 있으므로 숫자로 변환
        const numericStoreId = typeof storeId === 'string' ? parseInt(storeId, 10) || 0 : storeId;
        await createOrder(tableId, totalAmount, totalQuantity, orderItems, numericStoreId, tableName).then( res => {

          if(res.status === 200 || res.status === 201) {
            logger.log('createOrder',res.data)
            logger.log('isVeronaOrder', res.data)
            const orderNo = res.data.dailyOrderNo
            setCompleteList({orderItems, orderNo, tableId, totalAmount, totalQuantity})
            showModal();
            setOpenCart(false)
            
            if (categoryInCart && categoryInCart.length > 0) {
              logger.log("------------ 주문 완료 후 타이머 설정 시작 ------------");
              logger.log("현재 카테고리 배열:", JSON.stringify(categories));
              logger.log("타이머 정보:", JSON.stringify(categoryInCart));
              
              // 카테고리 정보 확인
              const hasValidCategories = categories && Array.isArray(categories) && categories.length > 0;
              if (!hasValidCategories) {
                logger.error("유효한 카테고리 정보가 없습니다");
                return;
              }
              
              // 각 카테고리 ID 출력
              const categoryIds = categories.map(cat => cat.id);
              const timerCategoryIds = categoryInCart.map(cat => cat.categoryId);
              logger.log("카테고리 ID 목록:", categoryIds);
              logger.log("타이머 설정할 카테고리 ID 목록:", timerCategoryIds);
              
              // 매칭되는 카테고리 확인
              const matchingIds = timerCategoryIds.filter(id => categoryIds.includes(id));
              logger.log("매칭된 카테고리 ID:", matchingIds);
              
              if (matchingIds.length === 0) {
                logger.error("타이머 설정할 수 있는 매칭 카테고리가 없습니다");
                return;
              }
              
              // 타이머 설정 함수 호출
              addTimeoutsByCategory(categories, categoryInCart);
              logger.log("------------ 타이머 설정 요청 완료 ------------");
            } else {
              logger.log("타이머를 설정할 카테고리 정보가 없습니다");
            }
            if(!isNativeFlag) {
              recordOrderEvent('cart', true, 
                orderItems.length, 
                totalAmount, 
                { 
                  storeId: userInfo?.storeId || 'unknown',
                  tableName: tableName,
                  orderItems: orderItems,
                  totalQuantity: totalQuantity,
                  orderNo: orderNo 
                  }
                );
              } else {
                if (window.Android !== undefined) {
                  const pinpointData = preparePinpointData({
                    type: 'attr', 
                    data: {
                      function: 'cart', 
                      orderItems: orderItems, 
                      totalAmount: totalAmount, 
                      storeId: userInfo?.storeId || 'unknown', 
                      tableName: tableName, 
                      totalQuantity: totalQuantity, 
                      orderNo: orderNo
                    }
                  });
                  
                  try {
                    const jsonString = safeStringify(pinpointData);
                    window.Android.postMessage(jsonString);
                  } catch (error) {
                    logger.error('Pinpoint 데이터 전송 실패:', error);
                  }
                }
              }
            logger.log('isVeronaOrder', res.data)
            if(res.data.isVeronaOrder) {
              if(!isBrowser) {
                if(window.ReactNativeWebView !== undefined)
                  window.ReactNativeWebView.postMessage(JSON.stringify({'type': 'veronaOrder', 'data': res.data}));
                else if(window.Android !== undefined)
                  window.Android.postMessage(JSON.stringify({'type': 'veronaOrder', 'data': res.data}));
              }
            }
          }
          setClickOrder(false);
        },
        e => {
          setClickOrder(false);
        })
      } catch(e) {
        setClickOrder(false);
      }

  }

  const showModal = () => {
    resetOrderCart();
    setOpenComplete(true);
    logger.log('showModal')
  }

  const renderModifiers = (modifiers: IOrderModifiers[], depth = 0) => {
    return modifiers.map((modifier: IOrderModifiers) => (
      <>
      <Box key={modifier.modifierId}>
        <Box
          key={`modifier-${modifier.modifierId}-${depth}`}
          sx={{
            paddingLeft: `${depth * 1.2}rem`,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.6rem",
              fontWeight: "500",
              flex: 1,
              textAlign: "left",
            }}
          >
           <span style={{color: '#0085FF'}}>{t(`optionGroupNames.${modifier.modifierGroupId}.${currentLng}`)} :</span> {t(`optionNames.${modifier.modifierId}.${currentLng}`)}
          </Typography>
          <Typography
            sx={{
              fontSize: "1.6rem",
              fontWeight: "600",
              color: "#F72E13",
              textAlign: "right",
              width: "4.0rem",
            }}
          >
            x{modifier.quantity}
          </Typography>
          <Typography
            sx={{
              fontSize: "1.6rem",
              fontWeight: "600",
              color: "#F72E13",
              textAlign: "right",
              width: "6.0rem",
            }}
          >
            ${(modifier.modifierPrice / 100).toFixed(2)}
          </Typography>
        </Box>
      </Box>
      {modifier.orderModifiers && modifier.orderModifiers.length > 0 && (
        <Box sx={{ width: "100%" }}>{renderModifiers(modifier.orderModifiers, depth + 1)}</Box>
      )}
      </>
    ));
  };

  return (
    <>
    <Box sx={{width: '60.0rem', height: height }}>
      <Box sx={{display: 'flex', flexDirection: 'row',  padding: '2.0rem 1.9rem', justifyContent: 'flex-start', alignItems: 'center'}}>
          <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <Typography sx={{ml: '1.0rem', fontSize: '2.8rem', fontWeight: '700', fontStyle: 'normal', lineHeight: 'normal', color: '#000', letterSpacing: '.056rem', height: '4.2rem'}}>Cart</Typography>
            <div className="touch-area" style={{position: 'absolute',right: '3.1rem',top: '1.9rem',}} onClick={() => {
                  setOpenCart(false)
                }}>
              <img style={{width: '3rem', height: '3rem'}}
                src={process.env.PUBLIC_URL+'/assets/images/close.svg'}
                alt="close"
              />
            </div>
          </Box>
      </Box>

      <List style={{ overflow: 'overlay', overflowX: 'hidden', height: `calc(100% - 34rem)`, marginTop: '.4rem' }}>
          {cart.slice().reverse().map((item: ICart) => {
            return(
              <Box key={`cart-${item.itemId}`} sx={{ justifyContent: 'center', mx: '2.5rem',}}>
                {item.newItem && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: '1.0rem' }}>
                    <Typography sx={{fontSize: '2.0rem', fontWeight: 'bold',background: '#F72E13', borderRadius: '2.0rem', p: '0rem 2.0rem', width: '8.0rem', color: '#fff', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>New</Typography>
                  </Box>
                )}
                <Typography sx={{width: '54.0rem', fontSize: '2.1rem', fontWeight: '600', fontStyle: 'normal', letterSpacing: '-.02rem'}}>{t(`itemNames.${item.itemId}.${currentLng}`)}</Typography>
                { item.orderModifiers && item.orderModifiers.length !== 0 ?
                  <Box sx={{background: '#F0F0F0', borderRadius: '1.0rem', padding: '1.3rem 1.2rem', alignItems: 'flex-start', gap: '1.0rem'}}>
                    {renderModifiers(item.orderModifiers)}
                  </Box>
                :<></>}
                <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                  <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773', letterSpacing: '-.016rem'}}>Regular Price</Typography>
                  <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773',letterSpacing: '-.016rem', width: '6.5rem', textAlign: 'end'}}>${(item.itemPrice/100).toFixed(2)}</Typography>
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                  <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773', letterSpacing: '-.016rem'}}>Option Price</Typography>
                  <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773',letterSpacing: '-.016rem', width: '6.5rem', textAlign: 'end'}}>${((item.modifierPrice || 0)/100).toFixed(2)}</Typography>
                </Box>
                <Typography sx={{fontSize: '2.0rem', fontWeight: '600', letterSpacing:'-.02rem', textAlign: 'right', mt: '.5rem'}}>${((item.subtotalAmount || 0)/100).toFixed(2)}</Typography>
                <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', my: '2.0rem', gap: '5.0rem'}}>
                  <ButtonBase sx={{color:'#F72E13', borderRadius: '3.0rem', border: '2px solid #F72E13', backgroundColor: '#fff', width: '19.6rem', p: '.9rem 3.1rem', gap: '1.0rem', alignItems: 'center'}}
                    onClick={() => handleDeleteItem(item)}>
                    <Typography sx={{color:'#F72E13', fontSize: '1.6rem', fontStyle: 'normal', fontWeight: '600', letterSpacing: '-.016rem'}}>Remove</Typography>
                  </ButtonBase>
                  <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '2.6rem', width: '18.4rem',
                    padding: '.5rem .9rem', borderRadius: '3.0rem', backgroundColor: '#fff', border: '2px solid #E9E9E9'}}>
                    <IconButton sx={{p : '0rem'}}
                      aria-label="minus"
                      onClick={() => optimizedHandleSubCount(item)}
                    >
                      <img style={{width: '2.4rem', height: '2.4rem'}}
                        src={process.env.PUBLIC_URL+'/assets/images/minus.svg'}
                        alt="minus"
                      />
                    </IconButton>
                    <Typography id="item-price" sx={{fontSize: '2.2rem', fontWeight: '700', fontStyle: 'normal', width: '4.5rem', textAlign: 'center'}}>
                      {item.quantity}
                    </Typography>
                    <IconButton  sx={{p : '0rem'}}
                      aria-label="plus"
                      onClick={() => optimizedHandleAddCount(item)}
                    >
                      <img style={{width: '2.4rem', height: '2.4rem'}}
                        src={process.env.PUBLIC_URL+'/assets/images/add.svg'}
                        alt="add"
                      />
                    </IconButton>
                  </Box>
     
                </Box>
                <Divider sx={{ width: '54.0rem', mt: '.1rem', alignSelf: 'center', borderBlockColor: '#d8d8d8', mb: '2.0rem'}}/>
              </Box>
            )
          })}
      </List>
      <Box sx={{position: 'absolute', bottom: '0', left: '0', right: '0', display: 'flex', flexDirection: 'column', mb: '2.3rem', mx: '2.5rem', justifyContent: 'center', alignItems: 'center'}}>
        <Divider sx={{display: 'flex', width: '54.0rem', height: '2px', backgroundColor: '#000', mt: '.3rem'}}/>
       
        <Box sx={{display: 'flex', flexDirection: 'row', width: '100%', mt: '1.5rem', justifyContent: 'flex-end', alignItems: 'center'}}>
          <Typography sx={{fontSize: '2.2rem', fontWeight: '500', fontStyle: 'normal', letterSpacing: '-.022rem', lineHeight: '2.4rem'}}>Qty </Typography>
          <Typography sx={{fontSize: '2.4rem', fontWeight: '700', fontStyle: 'normal', width: '6.7rem', textAlign: 'end', lineHeight: '2.4rem', mr: '1.0rem' }}>{totalCount}</Typography>
        </Box>
        <Box sx={{ width: '54.0rem', display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', mt: '1.5rem'}}>
            <Box sx={{display: 'flex', flexDirection: 'row', padding: '1.2rem .8rem 0rem .4rem', justifyContent: 'flex-end', height: '3.6rem', backgroundColor: '#FFD600', width: '54.0rem'}}>
              <Typography sx={{fontSize: '2.2rem', fontWeight: '600', fontStyle: 'normal', letterSpacing: '-.022rem', lineHeight: '2.2rem'}}>Sub Total </Typography>
              <Typography sx={{fontSize: '2.6rem', fontWeight: '700', fontStyle: 'normal', letterSpacing: '-.026rem', lineHeight: '2.2rem', textAlign: 'end', marginLeft: '0.6rem'}}>${(totalPrice/100).toFixed(2)}</Typography>
            </Box>
        </Box>
        <Box sx={{display: 'flex', flexDirection: 'row', padding: '.4rem .8rem .8rem .0rem', justifyContent: 'flex-end', height: '3.6rem', backgroundColor: '#FFD600', width: '54.0rem'}}>
            <Typography sx={{color: '#A98E00', fontSize: '1.6rem', fontWeight: '600', fontStyle: 'normal', letterSpacing: '0rem', lineHeight: '2.2rem'}}>(Tax not included in the price)</Typography>
        </Box>
        <Box sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex', my: '2.0rem' }}>
          <OrderButton handleOrderNow={handleOrderMode} clickOrder={clickOrder}/>
        </Box>
      </Box> 
    </Box>
    </>
  )
}

export default Cart


